import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { formatBoostMultiplier } from '../section-list/utils';
import { rewardHubV2Bunja } from '../state';

interface Props {
  css?: Styles;
}

export const TradfiBoostBanner = ({ css: cssProp }: Props) => {
  const { isTradfiBoostEnabledAtom, boostMultiplierAtom } = useBunja(rewardHubV2Bunja);
  const isEnabled = useAtomValue(isTradfiBoostEnabledAtom);
  const multiplier = useAtomValue(boostMultiplierAtom);

  if (!isEnabled) return null;

  const multipleLabel = `${formatBoostMultiplier(multiplier)}x`;

  const description = (
    <FormattedMessage
      id='Reward_hub.r260728.Tradfi_boost_description'
      values={{
        multiple: multipleLabel,
        b: (chunks) => <span className={css({ color: 'content.accent.promotion' })}>{chunks}</span>,
      }}
    />
  );

  return (
    <div
      className={css(
        {
          bgColor: 'background.accent.promotion.tint',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.accent.promotion/40',
          rounded: 8,
          mb: { base: 0, smallDesktop: 80 },
          py: { base: 12, smallDesktop: 16 },
          px: { base: 12, smallDesktop: 20 },
        },
        cssProp,
      )}
    >
      {/* mobile */}
      <div className={stack({ gap: 2, smallDesktop: { display: 'none' } })}>
        <div className={hstack({ gap: 4, color: 'content.accent.promotion' })}>
          <span className={css({ textStyle: 'Body/S/Strong' })}>
            <FormattedMessage id='Reward_hub.r260728.Tradfi_boost_title' values={{ multiple: multipleLabel }} />
          </span>
          <BoltIcon size={16} />
        </div>
        <p className={css({ textStyle: 'Body/XS/Regular', color: 'content.default.level-3' })}>{description}</p>
      </div>

      {/* desktop */}
      <div className={hstack({ gap: 16, display: 'none', smallDesktop: { display: 'flex' } })}>
        <span
          className={css({ textStyle: 'Title/L/Monospace', color: 'content.accent.promotion', whiteSpace: 'nowrap' })}
        >
          {multipleLabel}
        </span>
        <div className={css({ w: '1px', h: 34, flexShrink: 0, bgColor: 'border.accent.promotion/40' })} />
        <div className={stack({ gap: 2 })}>
          <p className={css({ textStyle: 'Body/M/Strong', color: 'content.accent.promotion' })}>
            <FormattedMessage id='Reward_hub.r260731.Tradfi_boost_active' />
          </p>
          <p className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-3' })}>{description}</p>
        </div>
      </div>
    </div>
  );
};
